import { useEffect } from "react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBoundStore } from "../../../Store/store";
import { BaseCard } from "../../../Components/Cards/BaseCard";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faDownload } from "@fortawesome/free-solid-svg-icons";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import { PrimaryButton } from "../../../Components/Buttons/PrimaryButton";

export const AdminInvoiceDetail = () => {
    const {
        id,
    } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isRegenerating, setIsRegenerating] = React.useState(false);

    const invoiceDetail = useBoundStore((state) => state.invoiceDetail);
    const getInvoice = useBoundStore((state) => state.getInvoice);
    const regenerateInvoice = useBoundStore((state) => state.regenerateInvoice);

    useEffect(() => {
        getInvoice(id);
    }, [getInvoice, id]);

    const DownloadLink = ({ url, fileName }) => {
        const handleDownload = () => {
            fetch(url)
                .then((response) => response.blob())
                .then((blob) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement("a");

                    if (!fileName.endsWith('.pdf')) {
                        fileName += '.pdf';
                    }

                    link.href = url;
                    link.download = fileName || "downloaded-file";
                    document.body.appendChild(link);

                    link.click();

                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    console.error("Error fetching the file:", error);
                });
        };

        return (
            <div>
                <BlackButton type="primary" onClick={handleDownload}>
                    <FontAwesomeIcon className="group-hover:text-primary" icon={faDownload} />
                </BlackButton>
            </div>
        );
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
            case 'open':
                return <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('general.status.open')}</div>;
            case 'paid':
                return <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.paid')}</div>;
            default:
                return <div className={'px-2 py-1 bg-gray-300 text-gray-900 rounded-full text-xs opacity-70'}>{status}</div>;
        }
    };

    const handleRegenerateInvoice = async () => {
        setIsRegenerating(true);
        try {
            await regenerateInvoice(id);
            await getInvoice(id);
        } finally {
            setIsRegenerating(false);
        }
    };

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4 items-center'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('invoices.detail_title')}</h1>
                        <div className={"flex flex-row group items-center gap-2"} onClick={() => {
                            navigate(-1);
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft} className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'} />
                            <div className={'text-sm text-gray-500 my-auto cursor-pointer'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <div className={'md:col-span-1 space-y-4'}>
                    <BaseCard border={false}>
                        <h2 className={'text-lg font-semibold mb-4'}>{t('invoices.info_title')}</h2>

                        <div className="space-y-3">
                            <div>
                                <p className="font-bold text-sm text-gray-600">{t('invoices.invoice_number')}:</p>
                                <p className="text-sm">{invoiceDetail?.invoice_number_exact || '-'}</p>
                            </div>

                            <div>
                                <p className="font-bold text-sm text-gray-600">{t('shifts.freelancer')}:</p>
                                <p className="text-sm">{invoiceDetail?.freelancer ? `${invoiceDetail.freelancer.firstname} ${invoiceDetail.freelancer.lastname}` : '-'}</p>
                            </div>

                            <div>
                                <p className="font-bold text-sm text-gray-600">{t('invoices.amount')}:</p>
                                <p className="text-sm">€ {invoiceDetail?.amount || '-'}</p>
                            </div>

                            <div>
                                <p className="font-bold text-sm text-gray-600">{t('general.fields.status')}:</p>
                                <div className="mt-1 flex flex-wrap gap-2">
                                    {invoiceDetail?.status ? getStatusBadge(invoiceDetail.status) : '-'}
                                    {invoiceDetail?.ready_for_exact === 1 && (
                                        <div className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>
                                            {t('invoices.sent_to_exact')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <p className="font-bold text-sm text-gray-600">{t('invoices.created_at')}:</p>
                                <p className="text-sm">{invoiceDetail?.created_at?.formatted || invoiceDetail?.created_at?.locale ? new Date(invoiceDetail.created_at.locale || invoiceDetail.created_at.formatted).toLocaleDateString('nl-NL') : '-'}</p>
                            </div>
                        </div>
                    </BaseCard>
                </div>

                <div className={'md:col-span-2 space-y-4'}>
                    <BaseCard border={false}>
                        <div className={'flex flex-wrap justify-between mb-4'}>
                            <h1 className={'text-lg font-semibold'}>{t('invoices.document')}</h1>

                            <div className="flex gap-2">
                                <PrimaryButton
                                    onClick={handleRegenerateInvoice}
                                    disabled={isRegenerating}
                                >
                                    {isRegenerating ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            {t('performance_reports.regenerating')}
                                        </div>
                                    ) : (
                                        t('invoices.regenerate')
                                    )}
                                </PrimaryButton>

                                {
                                    invoiceDetail?.document !== null && invoiceDetail?.document !== undefined && (
                                        <DownloadLink
                                            url={invoiceDetail?.document?.url}
                                            fileName={invoiceDetail?.document?.name}
                                        />
                                    )
                                }
                            </div>
                        </div>

                        {
                            invoiceDetail?.document !== null && invoiceDetail?.document !== undefined && invoiceDetail?.document.hasOwnProperty('url') && (
                                <embed src={invoiceDetail?.document?.url} width="100%" height="600px" type="application/pdf" />
                            )
                        }
                    </BaseCard>
                </div>
            </div>
        </div>
    )
}
